import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ReactLenis, useLenis } from "lenis/react";
import "lenis/dist/lenis.css";
import { Toaster } from "sonner";
import { BRAND } from "@/data/content";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Intro from "@/components/Intro";
import WhatWeDo from "@/components/WhatWeDo";
import Approach from "@/components/Approach";
import ServicesPreview from "@/components/ServicesPreview";
import OperatingModel from "@/components/OperatingModel";
import Focus from "@/components/Focus";
import Journey from "@/components/Journey";
import Technology from "@/components/Technology";
import HowWeWorkPreview from "@/components/HowWeWorkPreview";
import EngagementsPreview from "@/components/EngagementsPreview";
import Businesses from "@/components/Businesses";
import Global from "@/components/Global";
import Principles from "@/components/Principles";
import Careers from "@/components/Careers";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import { lazy, Suspense } from "react";
import { ApproachPage } from "@/pages/SitePages";
import { initTracking } from "@/utils/tracking";
import { SERVICES } from "@/data/content";
import { FloatingWhatsApp } from "@/components/WhatsApp";

/* route-level code splitting: non-homepage pages load on demand */
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const AboutPage = lazy(() => import("@/pages/About"));
const BusinessesPage = lazy(() => import("@/pages/Businesses"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ServicesLanding = lazy(() => import("@/pages/ServicesLanding"));
const PricingPage = lazy(() => import("@/pages/Pricing"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorks"));
const LocationsPage = lazy(() => import("@/pages/Locations"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const RequestProposal = lazy(() => import("@/pages/RequestProposal"));
const PayPage = lazy(() => import("@/pages/PayPage"));
const ServicePage = lazy(() =>
  import("@/components/ServicePage").then((m) => ({ default: m.ServicePage }))
);
const InsightsList = lazy(() =>
  import("@/pages/Insights").then((m) => ({ default: m.InsightsList }))
);
const InsightArticle = lazy(() =>
  import("@/pages/Insights").then((m) => ({ default: m.InsightArticle }))
);
const LegalIndex = lazy(() =>
  import("@/pages/Legal").then((m) => ({ default: m.LegalIndex }))
);
const legalLazy = (name) =>
  lazy(() => import("@/pages/Legal").then((m) => ({ default: m[name] })));
const Privacy = legalLazy("Privacy");
const Terms = legalLazy("Terms");
const Cancellation = legalLazy("Cancellation");
const Refund = legalLazy("Refund");
const PaymentBilling = legalLazy("PaymentBilling");
const ServiceDelivery = legalLazy("ServiceDelivery");
const InternationalPayments = legalLazy("InternationalPayments");
const Cookies = legalLazy("Cookies");
const Disclaimer = legalLazy("Disclaimer");
const Grievance = legalLazy("Grievance");

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Loader = () => (
  <motion.div
    data-testid="loader-overlay"
    className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-[#0A0906]"
    exit={{ opacity: 0 }}
    transition={{ duration: 1, ease: "easeInOut" }}
  >
    <motion.img
      src={BRAND.logo}
      alt="Logibyte Technologies"
      data-testid="loader-logo"
      className="h-24 w-24 rounded-full sm:h-28 sm:w-28"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
    />
    <motion.div
      className="mt-8 h-px w-0 bg-gradient-to-r from-transparent via-slate-500 to-transparent"
      animate={{ width: 160 }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
    />
  </motion.div>
);

const Home = () => (
  <main>
    <Hero />
    <Intro />
    <WhatWeDo />
    <Approach />
    <ServicesPreview />
    <OperatingModel />
    <Focus />
    <Journey />
    <Technology />
    <HowWeWorkPreview />
    <Businesses />
    <Global />
    <EngagementsPreview />
    <Principles />
    <Careers />
    <Contact />
  </main>
);

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        if (lenis) {
          lenis.scrollTo(hash, { offset: -80, immediate: prefersReducedMotion });
        } else {
          document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);      return () => clearTimeout(t);
    }
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash, lenis]);
  return null;
};

/* Normalize case-variant paths (/Locations -> /locations) — SPA equivalent of a
   permanent redirect; true 301 host/path handling is server/CDN level. */
const LowercaseRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const lower = pathname.toLowerCase();
  if (pathname !== lower) return <Navigate to={`${lower}${search}${hash}`} replace />;
  return null;
};

/* Premium 404 — unknown URLs render a branded page (not an empty shell) + noindex. */
const NotFound = () => {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Page Not Found | Logibyte Technologies";
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    const prev = robots.getAttribute("content");
    robots.setAttribute("content", "noindex, nofollow");
    return () => robots.setAttribute("content", prev || "");
  }, []);
  return (
    <main
      data-testid="page-not-found"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A0906] px-6 [color-scheme:dark]"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-[-20%] h-[60vh] w-[80vw] -translate-x-1/2 rounded-full bg-[#D4AF37]/[0.06] blur-[140px]"
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="relative text-center">
        <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#E7CE8B]">404</p>
        <h1 className="mt-6 font-serif text-4xl font-normal tracking-tight text-slate-50 sm:text-5xl">
          This page doesn&apos;t{" "}
          <span className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text">
            exist.
          </span>
        </h1>
        <p className="mx-auto mt-6 max-w-md text-[17px] font-normal leading-[1.75] text-stone-400">
          The address may be outdated or incorrect. Let&apos;s get you back on
          track.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <button
            data-testid="not-found-home-btn"
            onClick={() => navigate("/")}
            className="rounded-xl bg-gradient-to-r from-[#E7CE8B] to-[#C9A227] px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#231a05] transition-all duration-500 hover:brightness-110"
          >
            Return Home
          </button>
          <button
            data-testid="not-found-services-btn"
            onClick={() => navigate("/services")}
            className="rounded-xl border border-[#D4AF37]/40 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#E7CE8B] transition-colors duration-500 hover:bg-[#D4AF37]/10"
          >
            Explore Services
          </button>
        </div>
      </div>
    </main>
  );
};

export default function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initTracking();
    // Keep preview / staging hosts out of Google — production SEO signals point
    // only to https://www.logibytetechnologies.com/
    if (!/(^|\.)logibytetechnologies\.com$/.test(window.location.hostname)) {
      let robots = document.head.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    }
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-[#0A0906] font-sans text-slate-100 antialiased selection:bg-[#D4AF37]/30 selection:text-white">
      <BrowserRouter>
        <ReactLenis
          root
          options={{
            lerp: 0.09,
            smoothWheel: !prefersReducedMotion,
            syncTouch: false,
          }}
        >
          <ScrollManager />
          <LowercaseRedirect />
          <AnimatePresence>{loading && <Loader key="loader" />}</AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: loading ? 0 : 1 }}
            transition={{ duration: 1.2 }}
          >
            <Navbar />
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/approach" element={<ApproachPage />} />
              <Route path="/services" element={<ServicesLanding />} />
              {SERVICES.map((s) => (
                <Route
                  key={s.slug}
                  path={`/services/${s.slug}`}
                  element={<ServicePage service={s} />}
                />
              ))}
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/businesses" element={<BusinessesPage />} />
              <Route path="/locations" element={<LocationsPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/insights" element={<InsightsList />} />
              <Route path="/insights/:slug" element={<InsightArticle />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/request-proposal" element={<RequestProposal />} />
              <Route path="/pay" element={<PayPage />} />
              <Route path="/legal" element={<LegalIndex />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cancellation-policy" element={<Cancellation />} />
              <Route path="/refund-policy" element={<Refund />} />
              <Route path="/payment-billing-policy" element={<PaymentBilling />} />
              <Route path="/service-delivery-policy" element={<ServiceDelivery />} />
              <Route path="/international-payments-policy" element={<InternationalPayments />} />
              <Route path="/cookie-policy" element={<Cookies />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/grievance-redressal" element={<Grievance />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <Footer />
          </motion.div>
          <FloatingWhatsApp />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#12100A",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#E2E8F0",
              },
            }}
          />
        </ReactLenis>
      </BrowserRouter>
    </div>
  );
}
